import { createSingleInterpolatedTranslationKey, TranslatableText } from '@dreryk/react-common';

import { appointmentsTranslationKeys } from '../../../translations';
import { SectionSavePayload } from '../MedicalProfileModal.types';

import { RejectedSectionSaveResult, SectionSaveFault, SectionSaveResult } from './MedicalProfileSaveAction.types';

const SURVEY_NOT_UPDATABLE_FAULT_CLASS = 'egabinet-survey-frontend-api:SurveyNotUpdatableFault';
const NO_ANSWERS_PROVIDED_FAULT_CLASS = 'egabinet-survey-frontend-api:NoAnswersProvidedFault';

export class SurveyMissingError extends Error {}

const readFaultClass = (reason: unknown): string | undefined => {
   if (typeof reason === 'object' && reason !== null && 'class' in reason) {
      const faultClass = (reason as { class: unknown }).class;
      return typeof faultClass === 'string' ? faultClass : undefined;
   }
   return undefined;
};

const classifyFault = (reason: unknown): SectionSaveFault => {
   if (reason instanceof SurveyMissingError) {
      return 'SurveyMissing';
   }
   switch (readFaultClass(reason)) {
      case SURVEY_NOT_UPDATABLE_FAULT_CLASS:
         return 'SurveyNotUpdatable';
      case NO_ANSWERS_PROVIDED_FAULT_CLASS:
         return 'NoAnswersProvided';
      default:
         return 'Unknown';
   }
};

export const aggregateSaveResults = (
   payloads: SectionSavePayload[],
   settledResults: PromiseSettledResult<unknown>[]
): SectionSaveResult[] =>
   payloads.map((payload, index) => {
      const settledResult = settledResults[index];
      if (settledResult.status === 'fulfilled') {
         return { status: 'fulfilled', payload: payload };
      }
      return { status: 'rejected', payload: payload, fault: classifyFault(settledResult.reason) };
   });

export const getRejectedResults = (results: SectionSaveResult[]): RejectedSectionSaveResult[] =>
   results.filter((result): result is RejectedSectionSaveResult => result.status === 'rejected');

export const describeSaveFailure = (
   rejectedResults: RejectedSectionSaveResult[],
   resolveTabLabel: (templateCode: string) => string
): TranslatableText => {
   if (rejectedResults.every((result) => result.fault === 'SurveyNotUpdatable')) {
      return appointmentsTranslationKeys.medicalProfile.save.surveyNotUpdatable;
   }
   const failedSectionNames = rejectedResults.map(
      (result) => `${resolveTabLabel(result.payload.templateCode)}: ${result.payload.sectionTitle}`
   );
   return createSingleInterpolatedTranslationKey(
      appointmentsTranslationKeys.medicalProfile.save.failure,
      'sections',
      Array.from(new Set(failedSectionNames)).join(', ')
   );
};
